import type { FC } from 'react';

const archSpinnerDurationMs = 1400;
const archSpinnerTrackColor = '#E7E2E2';
const archSpinnerBrandColor = 'var(--color-contentAccentBrand, #0041E7)';
const archSpinnerStrokeWidth = 2.5;
const archSpinnerArchPath =
  'M41 2C62.54 2 80 19.46 80 41V87C80 89.76 77.76 92 75 92H7C4.24 92 2 89.76 2 87V41C2 19.46 19.46 2 41 2Z';
const archSpinnerOOuterPath =
  'M17.9736 13.5192C17.9736 5.038 16.494 1.52242 12.4261 1.52242C8.83871 1.52242 7.02656 3.98688 7.02656 12.5407C7.02656 20.9857 8.43196 24.4651 12.574 24.4651C16.2353 24.4651 17.9736 22.0366 17.9736 13.5192ZM0 13.3019C0 3.76943 6.69382 0 12.648 0C19.0089 0 25 3.7333 25 12.7219C25 22.3266 18.3063 25.9875 12.352 25.9875C5.99119 25.9875 0 22.0006 0 13.3019Z';
const archSpinnerOBarPath =
  'M23.6945 33.6562C23.0876 32.8671 20.7496 29.8274 20.7496 29.8274C20.5302 29.5422 20.1543 29.37 19.7509 29.37H5.24915C4.84575 29.37 4.46986 29.5422 4.2505 29.8274L1.30594 33.6556C1.21252 33.777 1.16295 33.9184 1.16295 34.0629V34.6472C1.16295 34.8421 1.32409 35 1.52292 35H23.4771C23.676 35 23.8371 34.8421 23.8371 34.6472V34.0629C23.8371 33.9184 23.788 33.7774 23.6945 33.6562Z';

// Inlined (not in index.css) so the spinner animates anywhere it mounts, including inside
// the pre-hydration RootLoader shell where app CSS may not have loaded yet.
const archSpinnerStylesheet = `
  @keyframes cake-od-arch-spinner-trace {
    0%     { stroke-dasharray: 5 95; stroke-dashoffset: 0; }
    20.6%  { stroke-dasharray: 5 95; stroke-dashoffset: -12; }
    22.6%  { stroke-dasharray: 9 91; stroke-dashoffset: -16; }
    36.3%  { stroke-dasharray: 9 91; stroke-dashoffset: -42; }
    38.3%  { stroke-dasharray: 5 95; stroke-dashoffset: -46; }
    63.8%  { stroke-dasharray: 5 95; stroke-dashoffset: -58; }
    65.8%  { stroke-dasharray: 9 91; stroke-dashoffset: -62; }
    79.5%  { stroke-dasharray: 9 91; stroke-dashoffset: -88; }
    81.5%  { stroke-dasharray: 5 95; stroke-dashoffset: -92; }
    100%   { stroke-dasharray: 5 95; stroke-dashoffset: -100; }
  }
  .cake-od-arch-spinner-tracer {
    stroke-dasharray: 5 95;
    stroke-dashoffset: 0;
    animation: cake-od-arch-spinner-trace ${archSpinnerDurationMs}ms linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .cake-od-arch-spinner-tracer {
      animation-duration: 1ms;
      animation-iteration-count: 1;
    }
  }
`;
const archSpinnerStylesheetMarkup = { __html: archSpinnerStylesheet } as const;

/**
 * The animated Opendoor "O" arch loading spinner: a brand-colored tracer running
 * around the arch outline above the static "O" mark.
 */
export const OpendoorArchSpinner: FC<{
  width?: number;
  height?: number;
}> = ({ width = 56, height = 68 }) => (
  <>
    <style dangerouslySetInnerHTML={archSpinnerStylesheetMarkup} />
    <svg
      width={width}
      height={height}
      viewBox="0 0 41 50"
      fill="none"
      overflow="visible"
      role="img"
      aria-label="Loading"
      xmlns="http://www.w3.org/2000/svg"
      // eslint-disable-next-line react/forbid-dom-props -- rendered inside the pre-provider RootLoader first-paint path (self-contained, no Tailwind dependency); inline style intentional
      style={{ overflow: 'visible' }}
    >
      <g transform="translate(20.5 24) scale(0.667) translate(-41 -47)">
        <path
          d={archSpinnerArchPath}
          fill="none"
          stroke={archSpinnerTrackColor}
          strokeWidth={archSpinnerStrokeWidth}
          strokeLinejoin="round"
          pathLength={100}
          vectorEffect="non-scaling-stroke"
        />
        <path
          className="cake-od-arch-spinner-tracer"
          d={archSpinnerArchPath}
          fill="none"
          stroke={archSpinnerBrandColor}
          strokeWidth={archSpinnerStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={100}
          vectorEffect="non-scaling-stroke"
        />
      </g>
      <g transform="translate(8 8)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d={archSpinnerOOuterPath}
          fill={archSpinnerTrackColor}
        />
        <path d={archSpinnerOBarPath} fill={archSpinnerTrackColor} />
      </g>
    </svg>
  </>
);
